import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Building2,
  ChevronRight,
  Eye,
  Globe,
  Inbox,
  LayoutGrid,
  LifeBuoy,
  MapPin,
  Settings as SettingsIcon,
  Tag,
  Users,
} from 'lucide-react';
import { getRepository } from '../config/repository';
import { canAccessModule } from '../core/security';
import { flattenBranches } from '../core/brandBranches';
import {
  Brand,
  MODULE_IDS,
  Organization,
  User,
  UserRole,
  UserStatus,
  isPlatformRole,
} from '../domain/entities';
import { cn } from '../ui/utils';

type PlatformStats = {
  totalOrgs: number;
  totalUsers: number;
  totalApps: number;
  pendingModuleReqs: number;
};

export function Dashboard({
  user,
  currentOrg,
  isImpersonating,
}: {
  user: User | null;
  currentOrg?: Organization | null;
  isImpersonating?: boolean;
}) {
  const navigate = useNavigate();
  const isSuperAdmin = isPlatformRole(user?.role);
  const isAdmin = user?.role === UserRole.ADMIN;

  const [launchpadBrands, setLaunchpadBrands] = useState<Brand[]>([]);
  const [launchpadUsers, setLaunchpadUsers] = useState<User[]>([]);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    const repo = getRepository();
    if (isSuperAdmin) {
      Promise.all([
        repo.getOrganizations(),
        repo.getUsers(),
        repo.getModuleRequests(),
      ]).then(([orgs, users, modReqs]) => {
        setPlatformStats({
          totalOrgs: orgs.length,
          totalUsers: users.filter((u: User) => u.orgId && u.orgId !== '__platform__').length,
          totalApps: orgs.reduce((sum: number, org: Organization) => sum + (org.enabledApps?.length ?? 0), 0),
          pendingModuleReqs: modReqs.filter((request: any) => request.status === 'pending').length,
        });
      });
      return;
    }

    Promise.all([
      repo.getBrands(),
      repo.getUsers(),
    ]).then(([brands, users]) => {
      setLaunchpadBrands(brands);
      setLaunchpadUsers(users);
    });
  }, [isSuperAdmin, currentOrg?.id]);

  const installedApps = currentOrg?.enabledApps?.length ?? 0;
  const activeStaff = launchpadUsers.filter(u => (u.status ?? UserStatus.ACTIVE) === UserStatus.ACTIVE).length;
  const registeredBrands = launchpadBrands.length;
  const activeBranches = flattenBranches(launchpadBrands).length;

  const orgApps = currentOrg?.enabledApps ?? [];
  const showCampaignCard = !isSuperAdmin && canAccessModule(user, MODULE_IDS.CAMPAIGN_MANAGER, undefined, orgApps);
  const showMenuCard = !isSuperAdmin && canAccessModule(user, MODULE_IDS.MENU_MANAGE, undefined, orgApps);
  const showSupportCard = !isSuperAdmin && canAccessModule(user, MODULE_IDS.SUPPORT_DESK, undefined, orgApps);
  const showMarketMapCard = !isSuperAdmin && canAccessModule(user, MODULE_IDS.MARKET_MAP, undefined, orgApps);

  const shellCardCount = isSuperAdmin
    ? 2
    : 1 + [showCampaignCard, showMenuCard, showSupportCard, showMarketMapCard].filter(Boolean).length;

  return (
    <div className="space-y-8">
      {isImpersonating && currentOrg && (
        <div className="flex items-center gap-3 px-5 py-3.5 bg-amber-50 border border-amber-200 rounded-2xl text-sm">
          <Eye size={16} className="text-amber-600 shrink-0" />
          <span className="text-amber-800 font-medium">
            You are viewing the Launchpad <strong>exactly as a user from <span className="underline">{currentOrg.name}</span> sees it</strong>.
            Apps and modules shown reflect that org&apos;s current configuration.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900">
            {isSuperAdmin ? 'Platform Dashboard' : isAdmin ? 'Admin Dashboard' : 'Dashboard'}
          </h2>
          <p className="text-sm text-slate-500">
            Welcome back, {user?.name}. Here&apos;s a high-level overview of {isSuperAdmin ? 'the entire platform' : isAdmin ? 'the platform' : 'your workspace'}.
          </p>
        </div>
        <div className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-2xl border border-emerald-100 flex items-center gap-2 text-sm font-bold">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          System Online
        </div>
      </div>

      {isSuperAdmin ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          <KpiCard label="Organisations" value={platformStats?.totalOrgs ?? '-'} Icon={Building2} iconClassName="bg-indigo-500" />
          <KpiCard label="Tenant Users" value={platformStats?.totalUsers ?? '-'} Icon={Users} iconClassName="bg-blue-500" />
          <KpiCard label="Apps Installed" value={platformStats?.totalApps ?? '-'} Icon={LayoutGrid} iconClassName="bg-emerald-500" />
          <KpiCard label="Module Requests" value={platformStats?.pendingModuleReqs ?? '-'} Icon={Inbox} iconClassName="bg-amber-500" />
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          <KpiCard label="Installed Apps" value={installedApps} Icon={LayoutGrid} iconClassName="bg-zinc-900" />
          <KpiCard label="Active Staff" value={activeStaff} Icon={Users} iconClassName="bg-indigo-500" />
          <KpiCard label="Registered Brands" value={registeredBrands} Icon={Tag} iconClassName="bg-emerald-500" />
          <KpiCard label="Active Branches" value={activeBranches} Icon={MapPin} iconClassName="bg-amber-500" />
        </div>
      )}

      <div className={cn('grid gap-5', getShellGridClass(shellCardCount))}>
        {isSuperAdmin && (
          <LaunchCard
            title="Organizations"
            description="Manage tenant organizations, plans, and billing."
            action="Manage Orgs"
            Icon={Building2}
            iconClassName="bg-zinc-900"
            accentClassName="bg-zinc-900/5"
            textClassName="text-zinc-900"
            onClick={() => navigate('/saas-admin/organizations')}
          />
        )}

        {isSuperAdmin ? (
          <LaunchCard
            title="Platform Health"
            description="Monitor platform-wide statistics, usage metrics, and system activity."
            action="View Health"
            Icon={BarChart3}
            iconClassName="bg-violet-500"
            accentClassName="bg-violet-500/5"
            textClassName="text-zinc-900"
            onClick={() => navigate('/saas-admin/health')}
          />
        ) : isAdmin ? (
          <LaunchCard
            title="System Settings"
            description="Manage brands, user accounts, and system-wide preferences."
            action="Configure System"
            Icon={SettingsIcon}
            iconClassName="bg-indigo-500"
            accentClassName="bg-indigo-500/5"
            textClassName="text-zinc-900"
            onClick={() => navigate('/system/brands')}
          />
        ) : null}

        {showCampaignCard && (
          <LaunchCard
            title="Aggregator Operations"
            description="Orchestrate brand promotions, discounts, and aggregator campaigns."
            action="Open Module"
            Icon={Globe}
            iconClassName="bg-teal-500"
            accentClassName="bg-teal-500/5"
            textClassName="text-teal-700"
            hoverClassName="hover:border-teal-400"
            onClick={() => navigate('/apps/aggregator')}
          />
        )}

        {showMenuCard && (
          <LaunchCard
            title="Menu Management"
            description="Build dual-language menus, manage ministry permits, and preview digital menus."
            action="Open Module"
            Icon={BookOpen}
            iconClassName="bg-indigo-600"
            accentClassName="bg-indigo-500/5"
            textClassName="text-indigo-700"
            hoverClassName="hover:border-indigo-400"
            onClick={() => navigate('/apps/menu-manage')}
          />
        )}

        {showSupportCard && (
          <LaunchCard
            title="Support & Ticketing"
            description="Report issues, track SLAs, and manage centralised helpdesk operations."
            action="Open Module"
            Icon={LifeBuoy}
            iconClassName="bg-violet-600"
            accentClassName="bg-violet-500/5"
            textClassName="text-violet-700"
            hoverClassName="hover:border-violet-400"
            onClick={() => navigate('/apps/support-desk')}
          />
        )}

        {showMarketMapCard && (
          <LaunchCard
            title="Market Intelligence"
            description="GIS command center for branch performance, competitor tracking, and live sales mapping."
            action="Open Module"
            Icon={MapPin}
            iconClassName="bg-cyan-600"
            accentClassName="bg-cyan-500/5"
            textClassName="text-cyan-700"
            hoverClassName="hover:border-cyan-400"
            onClick={() => navigate('/apps/market-map')}
          />
        )}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  Icon,
  iconClassName,
}: {
  label: string;
  value: string | number;
  Icon: React.ComponentType<{ size?: number }>;
  iconClassName: string;
}) {
  return (
    <div className="bg-white border border-zinc-200 p-5 rounded-2xl shadow-sm flex items-center gap-4">
      <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center text-white', iconClassName)}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
      </div>
    </div>
  );
}

function LaunchCard({
  title,
  description,
  action,
  Icon,
  iconClassName,
  accentClassName,
  textClassName,
  hoverClassName = 'hover:border-zinc-900',
  onClick,
}: {
  title: string;
  description: string;
  action: string;
  Icon: React.ComponentType<{ size?: number }>;
  iconClassName: string;
  accentClassName: string;
  textClassName: string;
  hoverClassName?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm hover:shadow-2xl transition-all text-left group flex flex-col h-full relative overflow-hidden',
        hoverClassName,
      )}
    >
      <div className={cn('absolute top-0 right-0 w-64 h-64 rounded-full -mr-32 -mt-32 group-hover:scale-110 transition-transform duration-500', accentClassName)} />
      <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform relative z-10', iconClassName)}>
        <Icon size={28} />
      </div>
      <h3 className="text-xl font-bold text-zinc-900 mb-2 relative z-10">{title}</h3>
      <p className="text-zinc-500 text-sm mb-6 flex-1 relative z-10">{description}</p>
      <div className={cn('flex items-center gap-3 font-bold text-sm uppercase tracking-widest relative z-10', textClassName)}>
        {action} <ChevronRight size={20} />
      </div>
    </button>
  );
}

function getShellGridClass(count: number): string {
  if (count >= 5) return 'grid-cols-2 md:grid-cols-5';
  if (count === 4) return 'grid-cols-2 md:grid-cols-4';
  if (count === 3) return 'grid-cols-3';
  if (count === 2) return 'grid-cols-2';
  return 'grid-cols-1';
}
